/**
 * The web's document store (web app Phase 5). Web only: the phone keeps its
 * own encrypted copy (documentStorageService + documentFileSync); the
 * browser keeps nothing and works straight against the server's copy.
 *
 * Same key hierarchy and envelope as the phone, byte for byte:
 *   - a fresh per-document key, wrapped (v2) under the owner's master key;
 *   - the file's base64, v2-encrypted under the document key, serialized
 *     as JSON — the envelope the phone writes and uploads;
 *   - a SHA-256 checksum of that base64, as the phone computes it.
 * Encryption runs in the browser through the WebCrypto shim of
 * react-native-aes-crypto; the server sees ciphertext and a wrapped key.
 *
 * The master key comes from DocumentEncryptionService, so a browser without
 * it throws VaultLockedError and the screen asks for the recovery code
 * (promptToUnlockVault; decision of 2026-09-18).
 */
import Aes from 'react-native-aes-crypto';

import http from '../http';
import { DocumentEncryptionService } from '../documentStorageService';
import {
  decrypt as v2Decrypt,
  encrypt as v2Encrypt,
  generateKey as v2GenerateKey,
  isV2Envelope,
  parseEnvelope,
  serializeEnvelope,
} from '../encryption/DocumentEncryptionV2';
import type { RemoteDocumentFile } from './documentFileSync';

export type CloudDocument = RemoteDocumentFile;

export interface CloudDocumentList {
  files: CloudDocument[];
  usedBytes: number;
  limitBytes: number;
}

export interface CloudDocumentMetadata {
  fileName: string;
  category: string;
  documentType: string;
  tags: string[];
}

/** The largest file the server takes (its cap is about a 56MB file). */
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

const encryptionService = new DocumentEncryptionService();
const CHUNK = 0x8000;

function bytesToBase64(bytes: Uint8Array): string {
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += CHUNK) {
    parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]));
  }
  return btoa(parts.join(''));
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

export function newDocumentId(): string {
  return `doc_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** The phone's type label for a file, from its name (PDF, Image, Word, Excel, Other). */
export function documentTypeFor(fileName: string, mimeType?: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf' || mimeType === 'application/pdf') return 'PDF';
  if (['jpg', 'jpeg', 'png', 'gif', 'heic', 'webp'].includes(ext) || mimeType?.startsWith('image/')) return 'Image';
  if (['doc', 'docx'].includes(ext)) return 'Word';
  if (['xls', 'xlsx'].includes(ext)) return 'Excel';
  return 'Other';
}

/** A file's name without its extension, as the default document name. */
export function displayNameFor(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return (dot > 0 ? fileName.slice(0, dot) : fileName).trim() || 'Document';
}

/**
 * The MIME type to open a document with. Documents the phone saved before it
 * recorded the type carry only its label (PDF, Image); fall back to that.
 */
export function effectiveMimeType(doc: { mimeType?: string | null; documentType?: string }): string {
  if (doc.mimeType) return doc.mimeType;
  const byLabel: Record<string, string> = { pdf: 'application/pdf', image: 'image/jpeg' };
  return byLabel[(doc.documentType || '').toLowerCase()] ?? '';
}

async function masterKey(): Promise<string> {
  // Throws VaultLockedError for an enrolled account whose key this browser
  // does not hold yet. A pre-recovery-code account's key comes from the
  // server's vault, as on the phone.
  const key = await encryptionService.getMasterRecoveryKey();
  if (key) return key;
  return encryptionService.autoGenerateMasterKey();
}

/** Another person's master key, opened from the copy they sealed for this successor. */
async function ownerMasterKey(targetUserId: string): Promise<string> {
  // Loaded here, not at module load: the trusted-party code brings the
  // crypto modules with it, which only this path needs.
  const { fetchAndOpenPrimaryMasterKey } = await import('../encryption/TrustedPartyAccess');
  return fetchAndOpenPrimaryMasterKey(targetUserId);
}

class CloudDocuments {
  /**
   * The signed-in person's files, or, with `targetUserId`, another
   * person's after their death is verified (server-gated; K5 / Step 5.6).
   */
  async list(targetUserId?: string): Promise<CloudDocumentList> {
    const query = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
    const res = await http.get(`/api/document-files${query}`);
    return res.data.data as CloudDocumentList;
  }

  /**
   * Encrypt a file in the browser and store it. The document id is new
   * unless one is given (an edit that replaces the file keeps its id).
   */
  async upload(
    file: Blob & { name?: string; type: string },
    meta: CloudDocumentMetadata,
    documentId: string = newDocumentId(),
    onStage?: (stage: 'encrypting' | 'uploading' | 'saving') => void,
  ): Promise<CloudDocument> {
    if (file.size > MAX_FILE_BYTES) {
      throw new Error('This file is too large to store. The limit is about 50MB.');
    }
    // A new document needs a Recovery Code on the account (2026-09-25).
    const key = await encryptionService.masterKeyForNewDocument();

    onStage?.('encrypting');
    const contentBase64 = bytesToBase64(new Uint8Array(await file.arrayBuffer()));
    const checksum = await Aes.sha256(contentBase64);
    const documentKey = await v2GenerateKey();
    const wrappedKey = serializeEnvelope(await v2Encrypt(documentKey, key));
    const envelope = serializeEnvelope(await v2Encrypt(contentBase64, documentKey));

    onStage?.('uploading');
    const presign = await http.post('/api/document-files/presign', {
      documentId,
      sizeBytes: envelope.length, // the envelope is ASCII: characters are bytes
    });
    const { uploadUrl, s3Key } = presign.data.data;
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: envelope,
    });
    if (!put.ok) throw new Error(`The upload failed (${put.status}). Try again.`);

    onStage?.('saving');
    const confirm = await http.post('/api/document-files/confirm', {
      documentId,
      s3Key,
      encryptedKey: wrappedKey,
      fileName: meta.fileName,
      category: meta.category,
      documentType: meta.documentType,
      mimeType: file.type || null,
      tags: meta.tags,
      checksum,
      fileSizeBytes: file.size,
    });
    return confirm.data.data as CloudDocument;
  }

  /**
   * Download and decrypt a document to a Blob of its own type. Another
   * person's document opens with the master key they sealed for this
   * successor (TrustedPartyAccess), never with the reader's own.
   */
  async download(documentId: string, targetUserId?: string): Promise<{ blob: Blob; doc: CloudDocument }> {
    const query = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
    const res = await http.get(`/api/document-files/${encodeURIComponent(documentId)}/download-url${query}`);
    const data = res.data.data as CloudDocument & { downloadUrl: string; encryptedKey: string };
    const key = targetUserId ? await ownerMasterKey(targetUserId) : await masterKey();

    const response = await fetch(data.downloadUrl);
    if (!response.ok) throw new Error(`The download failed (${response.status}). Try again.`);
    const raw = await response.text();
    if (!isV2Envelope(raw) || !isV2Envelope(data.encryptedKey)) {
      throw new Error('This document was saved in an older format and cannot be opened here.');
    }
    const documentKey = await v2Decrypt(parseEnvelope(data.encryptedKey), key);
    const contentBase64 = await v2Decrypt(parseEnvelope(raw), documentKey);
    // The app's Blob typing is React Native's, which takes neither typed
    // arrays nor a type option; the browser's Blob takes both.
    const BrowserBlob = Blob as unknown as new (parts: unknown[], options: { type: string }) => Blob;
    const blob = new BrowserBlob([base64ToBytes(contentBase64)], {
      type: effectiveMimeType(data) || 'application/octet-stream',
    });
    return { blob, doc: data };
  }

  async update(documentId: string, meta: Partial<CloudDocumentMetadata>): Promise<CloudDocument> {
    const res = await http.patch(`/api/document-files/${encodeURIComponent(documentId)}`, meta);
    return res.data.data as CloudDocument;
  }

  async remove(documentId: string): Promise<void> {
    await http.delete(`/api/document-files/${encodeURIComponent(documentId)}`);
  }
}

export const cloudDocuments = new CloudDocuments();
